import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import IdeaCard from './IdeaCard';
import SkeletonIdeaCard from './SkeletonIdeaCard';
import gsap from 'gsap';
import { Sparkles, ArrowRight, Zap } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const Home = () => {
  const { t } = useTranslation();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    fetchIdeas();
  }, []);

  useEffect(() => {
    // GSAP Animations
    const tl = gsap.timeline();

    if (titleRef.current) {
      tl.fromTo(titleRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: 'power4.out' }
      )
        .fromTo(".hero-subtitle",
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8 },
          "-=0.5"
        )
        .fromTo(".hero-btn",
          { scale: 0.9, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.5, stagger: 0.1 },
          "-=0.3"
        );
    }

    if (!loading && ideas.length > 0 && gridRef.current) {
      gsap.fromTo(gridRef.current.children,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, scrollTrigger: gridRef.current }
      );
    }
  }, [loading, ideas.length]);

  const fetchIdeas = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/ideas`);
      setIdeas(response.data.ideas);
      setError(null);
    } catch (err) {
      setError('Failed to load ideas. Please try again later.');
      console.error('Error fetching ideas:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-20">

      {/* Hero Section */}
      <section ref={heroRef} className="relative py-20 lg:py-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-primary/20 rounded-full blur-[120px] -z-10 opacity-50 mix-blend-screen animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-[800px] h-[600px] bg-secondary/10 rounded-full blur-[100px] -z-10 opacity-30"></div>

        <div className="container mx-auto px-4 text-center">
          <div ref={titleRef} className="mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-secondary">
            <Sparkles className="w-3 h-3" />
            <span>{t('hero.spark_label')}</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            {t('hero.title_start')} <br className="md:hidden" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-400 to-secondary animate-gradient-x">
              {t('hero.title_highlight')}
            </span>
          </h1>

          <p className="hero-subtitle text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/submit"
              className="hero-btn group relative px-8 py-4 rounded-full bg-white text-slate-900 font-bold hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              {t('hero.cta_submit')}
              <Zap className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
            </Link>
            <a
              href="#recent-ideas"
              className="hero-btn px-8 py-4 rounded-full border border-white/10 hover:bg-white/5 text-white font-medium hover:border-white/20 transition-all duration-300"
            >
              {t('hero.cta_explore')}
            </a>
          </div>
        </div>
      </section>

      {/* Ideas Grid Section */}
      <section id="recent-ideas" className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-12">
          <h2 className="text-3xl font-bold font-display">{t('home.recent_submissions')}</h2>
          <div className="h-px flex-grow bg-white/10 ml-8 hidden md:block"></div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <SkeletonIdeaCard key={n} />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-20 bg-surface/50 rounded-2xl border border-white/5">
            <p className="text-red-400">{error}</p>
          </div>
        ) : (
          <>
            {ideas.length === 0 ? (
              <div className="text-center py-24 bg-surface/30 rounded-3xl border border-white/5 border-dashed">
                <Rocket className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-300 mb-2">{t('home.no_ideas_title')}</h3>
                <p className="text-slate-500 mb-6">{t('home.no_ideas_desc')}</p>
                <Link to="/submit" className="text-primary hover:text-secondary font-bold flex items-center justify-center gap-1">
                  {t('home.launch_action')} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {ideas.map((idea) => (
                  <IdeaCard key={idea.id} idea={idea} />
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default Home;
